import { useMemo } from 'react';
import { useI18n } from '@/shared/i18n/useT';
import { useBookingT } from './i18n';
import {
  addDays,
  addMonths,
  buildMonthGrid,
  isInMonth,
  weekStartFor,
  type DayKey,
  type MonthKey,
} from './datetime';

interface Props {
  month: MonthKey;
  /** Days that actually have at least one free slot. Nothing else is clickable. */
  availableDays: ReadonlySet<DayKey>;
  selectedDay: DayKey | null;
  /** Today in the display timezone — used for the "today" ring, not for gating. */
  today: DayKey;
  /** Earliest month that can be reached; the back arrow stops there. */
  minMonth: MonthKey;
  /** Latest month that can be reached, from the event's booking horizon. */
  maxMonth: MonthKey;
  loading: boolean;
  onSelectDay: (day: DayKey) => void;
  onMonthChange: (month: MonthKey) => void;
}

/**
 * A real month grid. Only days the Worker returned slots for are enabled, so
 * the visitor never clicks into an empty column — which is the whole reason
 * this is a calendar and not a date input.
 */
export function MonthCalendar({
  month,
  availableDays,
  selectedDay,
  today,
  minMonth,
  maxMonth,
  loading,
  onSelectDay,
  onMonthChange,
}: Props) {
  const t = useBookingT();
  const { formatLocale } = useI18n();
  const weekStart = weekStartFor(formatLocale);
  const weeks = useMemo(
    () => buildMonthGrid(month, weekStart),
    [month, weekStart],
  );

  const monthLabel = new Intl.DateTimeFormat(formatLocale, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${month}-01T00:00:00Z`));

  // A known Sunday, so the header row can be produced from real dates.
  const weekdayLabels = useMemo(() => {
    const reference = '2024-01-07';
    const narrow = new Intl.DateTimeFormat(formatLocale, {
      weekday: 'narrow',
      timeZone: 'UTC',
    });
    const long = new Intl.DateTimeFormat(formatLocale, {
      weekday: 'long',
      timeZone: 'UTC',
    });
    return Array.from({ length: 7 }, (_, index) => {
      const date = new Date(`${addDays(reference, weekStart + index)}T00:00:00Z`);
      return { short: narrow.format(date), long: long.format(date) };
    });
  }, [formatLocale, weekStart]);

  const dayNumber = (day: DayKey): string => String(Number(day.slice(8, 10)));
  const longDate = (day: DayKey): string =>
    new Intl.DateTimeFormat(formatLocale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(`${day}T00:00:00Z`));

  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <button
          type="button"
          className="tap-target flex items-center justify-center rounded-full border border-border px-3 text-fg hover:bg-surface-3 disabled:opacity-40"
          disabled={month <= minMonth}
          onClick={() => onMonthChange(addMonths(month, -1))}
          aria-label={t('publicBooking.calendar.previousMonth')}
        >
          <span aria-hidden="true">‹</span>
        </button>
        <h3
          className="min-w-0 flex-1 text-center text-sm font-semibold text-fg"
          aria-live="polite"
        >
          {monthLabel}
        </h3>
        <button
          type="button"
          className="tap-target flex items-center justify-center rounded-full border border-border px-3 text-fg hover:bg-surface-3 disabled:opacity-40"
          disabled={month >= maxMonth}
          onClick={() => onMonthChange(addMonths(month, 1))}
          aria-label={t('publicBooking.calendar.nextMonth')}
        >
          <span aria-hidden="true">›</span>
        </button>
      </div>

      <div
        role="grid"
        aria-label={t('publicBooking.calendar.label')}
        aria-busy={loading}
        className="select-none"
      >
        <div role="row" className="grid grid-cols-7 gap-0.5 sm:gap-1">
          {weekdayLabels.map((weekday) => (
            <div
              key={weekday.long}
              role="columnheader"
              aria-label={weekday.long}
              className="py-1 text-center text-[0.6875rem] font-medium uppercase text-muted"
            >
              <span aria-hidden="true">{weekday.short}</span>
            </div>
          ))}
        </div>

        {weeks.map((week) => (
          <div
            key={week[0]}
            role="row"
            className="grid grid-cols-7 gap-0.5 sm:gap-1"
          >
            {week.map((day) => {
              const inMonth = isInMonth(day, month);
              const available = availableDays.has(day);
              const isSelected = day === selectedDay;
              const base =
                'tap-target flex w-full items-center justify-center rounded-token text-sm transition-colors';
              const look = !inMonth
                ? 'text-faint'
                : isSelected
                  ? 'bg-accent font-semibold text-accent-fg'
                  : available
                    ? 'bg-accent-soft font-semibold text-accent hover:bg-surface-3'
                    : 'text-faint';
              const ring =
                day === today && !isSelected
                  ? ' ring-1 ring-inset ring-border-strong'
                  : '';

              return (
                <div key={day} role="gridcell" className="p-0">
                  <button
                    type="button"
                    disabled={!available || loading}
                    aria-pressed={isSelected}
                    aria-label={
                      available
                        ? longDate(day)
                        : `${longDate(day)} — ${t('publicBooking.calendar.dayUnavailable')}`
                    }
                    onClick={() => onSelectDay(day)}
                    className={`${base} ${look}${ring} disabled:cursor-default`}
                  >
                    <span aria-hidden="true">{dayNumber(day)}</span>
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
